import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../layout/MainLayout';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import {
  Settings, Bell, Lock, Database, Mail, Globe, Save,
  RefreshCw, Download, Upload, AlertCircle, CheckCircle,
  Users, ScrollText, ChevronRight, BarChart3
} from 'lucide-react';
import apiService from '../../services/api';
import NotificationTemplates from './NotificationTemplates';
import InvitationSender from './InvitationSender';
import '../../styles/admin-settings.scss';

interface SystemSettings {
  [key: string]: {
    value: any;
    type: string;
    description: string;
    is_public: boolean;
    updated_at?: string;
  };
}

const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');
  const [settings, setSettings] = useState<SystemSettings>({});
  const [modifiedSettings, setModifiedSettings] = useState<Record<string, any>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.settings.getAll();
      if (response.data.status === 'success') {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setMessage({ type: 'error', text: 'Error al cargar las configuraciones' });
    } finally {
      setLoading(false);
    }
  };

  const getSettingValue = (key: string, defaultValue: any = '') => {
    if (modifiedSettings[key] !== undefined) {
      return modifiedSettings[key];
    }
    return settings[key]?.value ?? defaultValue;
  };

  const handleSettingChange = (key: string, value: any) => {
    setModifiedSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    if (Object.keys(modifiedSettings).length === 0) {
      setMessage({ type: 'error', text: 'No hay cambios para guardar' });
      return;
    }

    try {
      setSaving(true);
      await apiService.settings.updateBatch(modifiedSettings);

      setMessage({ type: 'success', text: 'Configuraciones guardadas exitosamente' });
      setModifiedSettings({});

      // Reload settings to get updated values
      await loadSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Error al guardar las configuraciones' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Estás seguro de que quieres restablecer todas las configuraciones a los valores predeterminados?')) {
      return;
    }

    try {
      setSaving(true);
      await apiService.settings.reset();

      setMessage({ type: 'success', text: 'Configuraciones restablecidas exitosamente' });
      setModifiedSettings({});

      // Reload settings
      await loadSettings();
    } catch (error) {
      console.error('Error resetting settings:', error);
      setMessage({ type: 'error', text: 'Error al restablecer las configuraciones' });
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiService.settings.export();

      // Create download link
      const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `settings_export_${new Date().toISOString().split('T')[0]}.json`;
      link.click();

      setMessage({ type: 'success', text: 'Configuraciones exportadas exitosamente' });
    } catch (error) {
      console.error('Error exporting settings:', error);
      setMessage({ type: 'error', text: 'Error al exportar las configuraciones' });
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      await apiService.settings.import(data);

      setMessage({ type: 'success', text: 'Configuraciones importadas exitosamente' });

      // Reload settings
      await loadSettings();
    } catch (error) {
      console.error('Error importing settings:', error);
      setMessage({ type: 'error', text: 'Error al importar las configuraciones' });
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="admin-settings admin-settings--loading">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4">Cargando configuraciones...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="admin-settings">
        {/* Message */}
        {message && (
          <div className={`settings-message settings-message--${message.type}`}>
            {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)} className="close-btn">×</button>
          </div>
        )}

        {/* Page Header */}
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">{(() => { const d = new Date(); return `${d.toLocaleDateString('es', { weekday: 'long' })} ${d.getDate()} de ${d.toLocaleDateString('es', { month: 'short' }).replace('.', '')} · Administrador`.toUpperCase(); })()}</div>
            <h1 className="heading-1">Configuración</h1>
            <p className="page-meta">
              Accede a la gestión de usuarios, reportes y auditoría del programa.
            </p>
          </div>
        </div>

        {/* Accesos: Usuarios y Auditoría */}
        <div className="settings-shortcuts">
          <button className="settings-shortcut" onClick={() => navigate('/admin/users')}>
            <span className="settings-shortcut__icon"><Users size={22} /></span>
            <span className="settings-shortcut__body">
              <span className="settings-shortcut__title">Usuarios</span>
              <span className="settings-shortcut__desc">Gestiona mentores, mentees y administradores</span>
            </span>
            <ChevronRight size={20} className="settings-shortcut__arrow" />
          </button>
          <button className="settings-shortcut" onClick={() => navigate('/admin/reports')}>
            <span className="settings-shortcut__icon"><BarChart3 size={22} /></span>
            <span className="settings-shortcut__body">
              <span className="settings-shortcut__title">Reportes</span>
              <span className="settings-shortcut__desc">Métricas y analíticas del programa</span>
            </span>
            <ChevronRight size={20} className="settings-shortcut__arrow" />
          </button>
          <button className="settings-shortcut" onClick={() => navigate('/admin/audit-logs')}>
            <span className="settings-shortcut__icon"><ScrollText size={22} /></span>
            <span className="settings-shortcut__body">
              <span className="settings-shortcut__title">Auditoría</span>
              <span className="settings-shortcut__desc">Registro de actividad y cambios del sistema</span>
            </span>
            <ChevronRight size={20} className="settings-shortcut__arrow" />
          </button>
        </div>

        {/* Plantillas de notificación del programa */}
        <InvitationSender />

        <NotificationTemplates />

        {false && (<>
        {/* Settings Tabs (ocultas por ahora — placeholders sin backend real) */}
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'general' ? 'settings-tab--active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            <Settings size={18} />
            General
          </button>
          <button
            className={`settings-tab ${activeTab === 'notifications' ? 'settings-tab--active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <Bell size={18} />
            Notificaciones
          </button>
          <button
            className={`settings-tab ${activeTab === 'security' ? 'settings-tab--active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <Lock size={18} />
            Seguridad
          </button>
          <button
            className={`settings-tab ${activeTab === 'program' ? 'settings-tab--active' : ''}`}
            onClick={() => setActiveTab('program')}
          >
            <Database size={18} />
            Programa
          </button>
        </div>

        {/* General Settings */}
        {activeTab === 'general' && (
          <Card animate>
            <CardHeader>
              <CardTitle>
                <Globe size={20} /> Configuración General
              </CardTitle>
            </CardHeader>
            <CardBody>
              <div className="settings-grid">
                <div className="form-group">
                  <label htmlFor="app_name">Nombre de la Aplicación</label>
                  <input
                    type="text"
                    id="app_name"
                    value={getSettingValue('app_name', 'Mentoring Platform')}
                    onChange={(e) => handleSettingChange('app_name', e.target.value)}
                  />
                  {settings['app_name']?.description && (
                    <small>{settings['app_name'].description}</small>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="app_logo">URL del Logo</label>
                  <input
                    type="text"
                    id="app_logo"
                    value={getSettingValue('app_logo', '/assets/logo.png')}
                    onChange={(e) => handleSettingChange('app_logo', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="default_user_role">Rol Predeterminado</label>
                  <select
                    id="default_user_role"
                    value={getSettingValue('default_user_role', 'mentee')}
                    onChange={(e) => handleSettingChange('default_user_role', e.target.value)}
                  >
                    <option value="mentee">Mentee</option>
                    <option value="mentor">Mentor</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="maintenance_mode">Modo Mantenimiento</label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={getSettingValue('maintenance_mode', false) === true || getSettingValue('maintenance_mode') === 'true'}
                      onChange={(e) => handleSettingChange('maintenance_mode', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="allow_self_registration">Permitir Auto-registro</label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={getSettingValue('allow_self_registration', true) === true || getSettingValue('allow_self_registration') === 'true'}
                      onChange={(e) => handleSettingChange('allow_self_registration', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="require_admin_approval">Requerir Aprobación</label>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={getSettingValue('require_admin_approval', true) === true || getSettingValue('require_admin_approval') === 'true'}
                      onChange={(e) => handleSettingChange('require_admin_approval', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Notifications Settings */}
        {activeTab === 'notifications' && (
          <Card animate>
            <CardHeader>
              <CardTitle>
                <Mail size={20} /> Configuración de Notificaciones
              </CardTitle>
            </CardHeader>
            <CardBody>
              <div className="settings-list">
                <div className="setting-item">
                  <div className="setting-item__info">
                    <h4>Notificaciones por Email</h4>
                    <p>Habilitar el envío de notificaciones por correo electrónico</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={getSettingValue('enable_email_notifications', true) === true || getSettingValue('enable_email_notifications') === 'true'}
                      onChange={(e) => handleSettingChange('enable_email_notifications', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-item__info">
                    <h4>Notificaciones SMS</h4>
                    <p>Habilitar el envío de notificaciones por SMS</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={getSettingValue('enable_sms_notifications', false) === true || getSettingValue('enable_sms_notifications') === 'true'}
                      onChange={(e) => handleSettingChange('enable_sms_notifications', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="session_reminder_hours">Horas antes para recordatorio</label>
                  <input
                    type="number"
                    id="session_reminder_hours"
                    value={getSettingValue('session_reminder_hours', 24)}
                    onChange={(e) => handleSettingChange('session_reminder_hours', parseInt(e.target.value))}
                    min="1"
                    max="72"
                  />
                  <small>Horas antes de la sesión para enviar recordatorio</small>
                </div>

                <div className="form-group">
                  <label htmlFor="notification_cleanup_days">Días para limpiar notificaciones</label>
                  <input
                    type="number"
                    id="notification_cleanup_days"
                    value={getSettingValue('notification_cleanup_days', 30)}
                    onChange={(e) => handleSettingChange('notification_cleanup_days', parseInt(e.target.value))}
                    min="7"
                    max="365"
                  />
                  <small>Días para mantener notificaciones leídas antes de eliminarlas</small>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Program Settings */}
        {activeTab === 'program' && (
          <Card animate>
            <CardHeader>
              <CardTitle>
                <Database size={20} /> Configuración del Programa
              </CardTitle>
            </CardHeader>
            <CardBody>
              <div className="settings-grid">
                <div className="form-group">
                  <label htmlFor="max_mentees_per_mentor">Máx. Mentees por Mentor</label>
                  <input
                    type="number"
                    id="max_mentees_per_mentor"
                    value={getSettingValue('max_mentees_per_mentor', 5)}
                    onChange={(e) => handleSettingChange('max_mentees_per_mentor', parseInt(e.target.value))}
                    min="1"
                    max="20"
                  />
                  <small>Número máximo de mentees que un mentor puede tener</small>
                </div>

                <div className="form-group">
                  <label htmlFor="min_sessions_per_month">Sesiones Mínimas/Mes</label>
                  <input
                    type="number"
                    id="min_sessions_per_month"
                    value={getSettingValue('min_sessions_per_month', 2)}
                    onChange={(e) => handleSettingChange('min_sessions_per_month', parseInt(e.target.value))}
                    min="1"
                    max="10"
                  />
                  <small>Número mínimo de sesiones mensuales requeridas</small>
                </div>

                <div className="form-group">
                  <label htmlFor="session_duration_minutes">Duración de Sesión (min)</label>
                  <input
                    type="number"
                    id="session_duration_minutes"
                    value={getSettingValue('session_duration_minutes', 60)}
                    onChange={(e) => handleSettingChange('session_duration_minutes', parseInt(e.target.value))}
                    min="15"
                    max="180"
                    step="15"
                  />
                  <small>Duración predeterminada de las sesiones</small>
                </div>

                <div className="form-group">
                  <label htmlFor="max_sessions_per_week">Máx. Sesiones/Semana</label>
                  <input
                    type="number"
                    id="max_sessions_per_week"
                    value={getSettingValue('max_sessions_per_week', 3)}
                    onChange={(e) => handleSettingChange('max_sessions_per_week', parseInt(e.target.value))}
                    min="1"
                    max="10"
                  />
                  <small>Número máximo de sesiones por semana</small>
                </div>

                <div className="form-group">
                  <label htmlFor="evaluation_frequency_days">Frecuencia de Evaluaciones (días)</label>
                  <input
                    type="number"
                    id="evaluation_frequency_days"
                    value={getSettingValue('evaluation_frequency_days', 30)}
                    onChange={(e) => handleSettingChange('evaluation_frequency_days', parseInt(e.target.value))}
                    min="7"
                    max="90"
                  />
                  <small>Días entre evaluaciones requeridas</small>
                </div>

                <div className="form-group form-group--full">
                  <label htmlFor="allowed_file_types">Tipos de Archivo Permitidos</label>
                  <input
                    type="text"
                    id="allowed_file_types"
                    value={getSettingValue('allowed_file_types', 'pdf,doc,docx,xls,xlsx,ppt,pptx,png,jpg,jpeg')}
                    onChange={(e) => handleSettingChange('allowed_file_types', e.target.value)}
                  />
                  <small>Extensiones de archivo permitidas (separadas por coma)</small>
                </div>

                <div className="form-group">
                  <label htmlFor="max_file_size_mb">Tamaño Máx. Archivo (MB)</label>
                  <input
                    type="number"
                    id="max_file_size_mb"
                    value={getSettingValue('max_file_size_mb', 10)}
                    onChange={(e) => handleSettingChange('max_file_size_mb', parseInt(e.target.value))}
                    min="1"
                    max="100"
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Integration Settings - New Tab for future */}
        {activeTab === 'integrations' && (
          <Card animate>
            <CardHeader>
              <CardTitle>
                <Globe size={20} /> Integraciones
              </CardTitle>
            </CardHeader>
            <CardBody>
              <div className="settings-list">
                <div className="setting-item">
                  <div className="setting-item__info">
                    <h4>Google Calendar</h4>
                    <p>Sincronizar sesiones con Google Calendar</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={getSettingValue('google_calendar_enabled', false) === true}
                      onChange={(e) => handleSettingChange('google_calendar_enabled', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-item__info">
                    <h4>Slack</h4>
                    <p>Enviar notificaciones a Slack</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={getSettingValue('slack_integration_enabled', false) === true}
                      onChange={(e) => handleSettingChange('slack_integration_enabled', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-item__info">
                    <h4>Microsoft Teams</h4>
                    <p>Integración con Microsoft Teams</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={getSettingValue('teams_integration_enabled', false) === true}
                      onChange={(e) => handleSettingChange('teams_integration_enabled', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </CardBody>
          </Card>
        )}
        </>)}
      </div>
    </MainLayout>
  );
};

export default AdminSettings;